import { useAppSelector } from "@/app/hooks";
import { LEASE_COMP_PINNED_FIELD_ORDER } from "@/constants/base";
import { selectLeaseCompFieldRules } from "@/features/survey/leaseCompsSelectors";
import {
  setLeaseCompFieldRules,
  updateLeaseCompFieldRule as updateLeaseCompFieldRuleAction,
} from "@/features/survey/leaseCompsSlice";
import {
  useReorderLeaseCompFieldMutation,
  useUpdateLeaseCompFieldRuleMutation,
  useUpdateLeaseCompFieldRulesBulkMutation,
} from "@/services/leaseCompFieldRules";
import { useGetTableConfigQuery } from "@/services/user";
import { getLeaseCompFieldOrder } from "@/shared/utils";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldSelect } from "@emerald-react/select";
import { useSortableList } from "@hooks/useSortable";
import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import type {
  Layouts,
  TableConfig,
} from "@survey-types/survey.leaseComp.types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import EditLeaseColumnField from "./EditLeaseColumnField";
import SortDropdown from "./SortDropdown";

interface EditLeaseColumnsProps {
  isOpen: boolean;
  onClose: () => void;
  layouts?: Layouts[];
}

interface LayoutOption {
  label: string;
  value: string;
  layoutId: string;
}

const RESET_LAYOUT_LABEL = "Reset to Default (Team Data)";
const RESET_LAYOUT_VALUE = "RESET_TO_DEFAULT_TEAM_DATA";

const EditLeaseColumns = ({
  isOpen,
  onClose,
  layouts,
}: EditLeaseColumnsProps) => {
  const dispatch = useDispatch();
  const [reorderLeaseCompFieldApi] = useReorderLeaseCompFieldMutation();
  const [updateLeaseCompFieldRule] = useUpdateLeaseCompFieldRuleMutation();
  const [updatePropertyFieldRuleBulkApi, { isLoading: isApplyingLayout }] =
    useUpdateLeaseCompFieldRulesBulkMutation();
  const surveyId = useAppSelector((state) => state.survey?.currentSurvey?.id);
  const leaseCompLayoutId = useAppSelector(
    (state) => state.survey?.currentSurvey?.leaseCompLayoutId,
  );
  const email = localStorage.getItem("email") ?? "";
  const { data: userResponse } = useGetTableConfigQuery(
    { email: email },
    { skip: !email },
  );
  const leaseCompTableConfig = userResponse?.currentUser?.leaseCompTableConfig;

  const [searchQuery, setSearchQuery] = useState("");
  const [sortedFields, setSortedFields] = useState<LeaseCompFieldRule[]>([]);
  const rightPanelListRef = useRef<HTMLDivElement | null>(null);
  const prevVisibleCountRef = useRef<number>(0);
  const prevPinnedCountRef = useRef<number>(0);

  // Fixed fields that are always pinned at top and cannot be reordered
  const dragDisabledItems = LEASE_COMP_PINNED_FIELD_ORDER;

  const sortHiddenFieldsWithPinnedFirst = (
    fields: LeaseCompFieldRule[],
    comparator?: (a: LeaseCompFieldRule, b: LeaseCompFieldRule) => number,
  ): LeaseCompFieldRule[] => {
    return [...fields].sort((a, b) => {
      const aPinnedIndex = dragDisabledItems.indexOf(a.keyName);
      const bPinnedIndex = dragDisabledItems.indexOf(b.keyName);
      const aPinned = aPinnedIndex !== -1;
      const bPinned = bPinnedIndex !== -1;

      if (aPinned && bPinned) return aPinnedIndex - bPinnedIndex;
      if (aPinned) return -1;
      if (bPinned) return 1;

      return comparator ? comparator(a, b) : a.order - b.order;
    });
  };

  const leaseCompFieldRulesData = useAppSelector(selectLeaseCompFieldRules);

  // Split into visible (isActive) and hidden (!isActive)
  const visibleFields: LeaseCompFieldRule[] = useMemo(() => {
    return leaseCompFieldRulesData
      ? ([...leaseCompFieldRulesData]
          .filter((f) => (f as LeaseCompFieldRule).isActive)
          .sort(
            (a, b) =>
              (a as LeaseCompFieldRule).order - (b as LeaseCompFieldRule).order,
          ) as LeaseCompFieldRule[])
      : [];
  }, [leaseCompFieldRulesData]);

  const hiddenFields: LeaseCompFieldRule[] = useMemo(() => {
    return leaseCompFieldRulesData
      ? sortHiddenFieldsWithPinnedFirst(
          leaseCompFieldRulesData.filter(
            (f) => !(f as LeaseCompFieldRule).isActive,
          ) as LeaseCompFieldRule[],
        )
      : [];
  }, [leaseCompFieldRulesData]);
  useEffect(() => {
    setSortedFields(hiddenFields);
  }, [hiddenFields]);

  useEffect(() => {
    const prev = prevVisibleCountRef.current;
    const curr = visibleFields.length;
    const pinnedCount = visibleFields.filter((f) =>
      dragDisabledItems.includes(f.keyName),
    ).length;

    if (!isApplyingLayout && curr - prev === 1) {
      const addedFieldIsPinned = pinnedCount > prevPinnedCountRef.current;
      rightPanelListRef.current?.scrollTo({
        top: addedFieldIsPinned ? 0 : rightPanelListRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    prevVisibleCountRef.current = curr;
    prevPinnedCountRef.current = pinnedCount;
  }, [visibleFields.length, isApplyingLayout]);

  const filteredHiddenFields = sortedFields.filter((f) =>
    f.displayName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const sortableVisibleFields = visibleFields.map((field) => ({
    ...field,
    id: field.key,
  }));

  const handleToggle = (fieldKey: string, makeActive: boolean) => {
    setLayoutName(""); // Reset layout selection when a field is toggled
    const field = leaseCompFieldRulesData?.find(
      (f) => (f as LeaseCompFieldRule).key === fieldKey,
    ) as LeaseCompFieldRule | undefined;
    if (!field || !surveyId) return;

    const targetList = makeActive ? visibleFields : hiddenFields;
    const order = dragDisabledItems.includes(field.keyName)
      ? getLeaseCompFieldOrder(
          field.keyName,
          targetList,
          visibleFields,
          hiddenFields,
        )
      : targetList.length + 1;

    dispatch(
      updateLeaseCompFieldRuleAction({
        key: fieldKey,
        isActive: makeActive,
        order,
      }),
    );

    updateLeaseCompFieldRule({
      surveyId,
      fieldRule: { ...field, isActive: makeActive, order },
      leaseCompLayoutId: "",
    }).catch(() => {
      Swal.fire({
        icon: "error",
        title: "Failed to update column",
        text: `Could not update "${field.displayName}". Please try again.`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
    });
  };

  const handleReorder = (
    _reorderedFields: LeaseCompFieldRule[],
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    setLayoutName(""); // Reset layout selection when a field is reordered
    const sourceField = visibleFields[sourceIndex];
    const destField = visibleFields[destinationIndex];
    if (!sourceField) return;

    if (
      dragDisabledItems.includes(sourceField.keyName) ||
      dragDisabledItems.includes(destField?.keyName)
    )
      return;

    // Do NOT dispatch reorderLeaseCompField here — the service's onQueryStarted
    // handles the optimistic Redux update with the correct visual active-field
    // indices. A direct dispatch here would cause a double-reorder.
    reorderLeaseCompFieldApi({
      surveyId: surveyId || "",
      order: destinationIndex,
      fieldId: sourceField.key,
      leaseCompLayoutId: "",
    }).catch(() => {
      Swal.fire({
        icon: "error",
        title: "Failed to reorder field",
        text: `Could not reorder "${sourceField.displayName}". Please try again.`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
    });
  };

  const { DndContextProps, SortableContextProps } = useSortableList({
    items: sortableVisibleFields,
    onReorder: handleReorder,
    idField: "id",
  });

  const clearAllColumns = () => {
    setLayoutName("");
    const visibleAllFields =
      leaseCompFieldRulesData?.filter((field) => field.isActive) ?? [];

    const visiblePinnedFields = visibleAllFields
      .filter((field) => dragDisabledItems.includes(field.keyName))
      .sort((a, b) => {
        const aIndex = dragDisabledItems.indexOf(a.keyName);
        const bIndex = dragDisabledItems.indexOf(b.keyName);
        const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return safeAIndex - safeBIndex;
      });

    const visiblePinnedFieldIds = new Set(
      visiblePinnedFields.map((field) => field.key),
    );

    const visibleNonPinnedFields = visibleAllFields.filter(
      (field) => !visiblePinnedFieldIds.has(field.key),
    );

    const orderedHiddenFieldsAfterClear = [
      ...visiblePinnedFields,
      ...hiddenFields,
      ...visibleNonPinnedFields,
    ];

    const clearedFields = orderedHiddenFieldsAfterClear.map((field, index) => ({
      key: field.key,
      isActive: false,
      displayName: field.displayName,
      order: index,
      keyName: field.keyName,
    }));

    if (surveyId) {
      dispatch(setLeaseCompFieldRules(clearedFields));
      updatePropertyFieldRuleBulkApi({
        surveyId,
        fieldRules: clearedFields,
        leaseCompLayoutId: "",
      });
    }
  };

  const sortOptionHandler = (value: string) => {
    if (value === "A to Z") {
      const sorted = [...hiddenFields].sort((a, b) =>
        a.keyName.localeCompare(b.keyName),
      );
      setSortedFields(sorted);
    } else if (value === "Z to A") {
      const sorted = [...hiddenFields].sort((a, b) =>
        b.keyName.localeCompare(a.keyName),
      );
      setSortedFields(sorted);
    } else {
      setSortedFields(hiddenFields);
    }
  };

  const layoutOptions = useMemo<LayoutOption[]>(
    () =>
      (layouts ?? [])
        .map((layout: Layouts) => ({
          label: layout.layoutName,
          value: layout.layoutId,
          layoutId: layout.layoutId,
        }))
        .concat({
          label: RESET_LAYOUT_LABEL,
          value: RESET_LAYOUT_VALUE,
          layoutId: RESET_LAYOUT_VALUE,
        }),
    [layouts],
  );

  const [layoutName, setLayoutName] = useState("");

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) return;

    const selectedLayout = layoutOptions.find(
      (layout) => layout.layoutId === leaseCompLayoutId,
    );

    setLayoutName(
      selectedLayout?.value ||
        (leaseCompLayoutId === RESET_LAYOUT_LABEL ? RESET_LAYOUT_VALUE : ""),
    );
  }, [isOpen, layoutOptions, leaseCompLayoutId]);

  const changeLayoutHandler = (
    _event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    value: string | string[],
  ) => {
    const selectedValue = value as string;
    let layout = null;
    setLayoutName(selectedValue);
    const layoutId = layoutOptions.find(
      (layout) => layout.value === selectedValue,
    )?.layoutId;

    if (
      selectedValue === RESET_LAYOUT_VALUE ||
      selectedValue === RESET_LAYOUT_LABEL
    ) {
      layout = leaseCompTableConfig;
    } else {
      layout = layouts?.find(
        (layout: Layouts) => layout.layoutId === selectedValue,
      )?.tableConfig;
    }

    const fieldRuleList = layout
      ?.map((config: TableConfig) => {
        const field = leaseCompFieldRulesData?.find((f) => {
          return (
            (f as LeaseCompFieldRule).keyName.toLocaleLowerCase() ===
              config.id.replace(/_/g, "").toLocaleLowerCase() ||
            ((f as LeaseCompFieldRule).keyName === "lessor" &&
              config.id === "LANDLORD")
          );
        }) as LeaseCompFieldRule | undefined;

        if (!field || !surveyId) {
          return;
        }
        return {
          key: field.key,
          isActive: config.isActive,
          displayName: field.displayName,
          order: field.order,
          keyName: field.keyName,
        };
      })
      .filter(
        (field: LeaseCompFieldRule | undefined): field is LeaseCompFieldRule =>
          field !== undefined,
      )
      .sort((a: LeaseCompFieldRule, b: LeaseCompFieldRule) => {
        const aIndex = dragDisabledItems.indexOf(a.keyName);
        const bIndex = dragDisabledItems.indexOf(b.keyName);
        const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return safeAIndex - safeBIndex;
      }) as LeaseCompFieldRule[];
    const fieldRuleListOrder = fieldRuleList.map((field, index) => ({
      ...field,
      order: index,
    }));
    dispatch(setLeaseCompFieldRules(fieldRuleListOrder));
    updatePropertyFieldRuleBulkApi({
      surveyId: surveyId || "",
      fieldRules: fieldRuleListOrder,
      leaseCompLayoutId: layoutId || "",
    });
  };

  return (
    <EmeraldModal
      opened={isOpen}
      onClose={onClose}
      closeOnOutsideClick={true}
      zIndex={1200}
      title="Edit Columns"
      headerText="Edit Columns"
      hasCloseIcon={true}
      className="edit-columns-modal"
    >
      <p className="edit-columns-modal__instructions">
        Click from the Available Columns section to customize your Column Layout
        and rearrange the fields in your preferred order.
      </p>
      {/* Left panel — Available (hidden) columns */}
      <div className="edit-columns-modal__body">
        <div className="edit-columns-modal__panel mr-4">
          <div className="panel-header">Available Columns</div>
          <div className="edit-columns-modal__panel-list border rounded-md overflow-hidden flex flex-col">
            <div className="edit-search-filters">
              <EmeraldInput
                placeholder="Search columns…"
                value={searchQuery}
                onChange={(e) =>
                  setSearchQuery(
                    (e as React.ChangeEvent<HTMLInputElement>).target.value,
                  )
                }
                className="m-4 edit-search"
              />
              <div className="edit-filters">
                <SortDropdown onSortOptionChange={sortOptionHandler} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredHiddenFields.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <EmeraldIcon
                    icon="view_column"
                    className="text-2xl text-slate-400 mb-2"
                  />
                  <p className="text-sm text-slate-500">
                    {searchQuery
                      ? "No columns are visible"
                      : "All columns are visible"}
                  </p>
                </div>
              ) : (
                filteredHiddenFields.map((field) => (
                  <EditLeaseColumnField
                    key={field.key}
                    field={field}
                    isDragDisabled={true}
                    side="left"
                    onToggle={handleToggle}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right panel — Column Layout (visible columns, drag & drop) */}
        <div className="edit-columns-modal__panel">
          <div className="header flex justify-between items-center">
            <div className="panel-header">Column Layout</div>
            <div
              className="clearColumns text-xs text-primary font-semibold cursor-pointer"
              onClick={clearAllColumns}
            >
              CLEAR ALL
            </div>
          </div>
          <div className="relative">
            <EmeraldSelect
              label={"Select Column Layout"}
              placeholder={
                isApplyingLayout
                  ? "Applying layout..."
                  : "Select a saved layout"
              }
              title={"Emerald"}
              editable={false}
              outlined
              options={layoutOptions}
              onChange={changeLayoutHandler}
              value={layoutName}
              disabled={isApplyingLayout}
            />
            {isApplyingLayout && (
              <div className="absolute right-10 top-1/2 -translate-y-1/2 flex items-center">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </div>
          <div
            className="edit-columns-modal__panel-list border rounded-md overflow-y-auto mt-3"
            ref={rightPanelListRef}
          >
            {visibleFields.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="edit-columns-modal__empty">
                  No columns are visible
                </div>
              </div>
            ) : (
              <DndContext {...DndContextProps}>
                <SortableContext {...SortableContextProps}>
                  {sortableVisibleFields.map((field) => (
                    <EditLeaseColumnField
                      key={field.key}
                      field={field}
                      isDragDisabled={dragDisabledItems.includes(field.keyName)}
                      side="right"
                      onToggle={handleToggle}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </EmeraldModal>
  );
};

export default EditLeaseColumns;
